import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { analyzeScriptStream, cancelAnalysis, getAnalysisStatus } from '@/services/scriptService';
import {
  agentLabel,
  buildAnalyseSubtext,
  inferCurrentAgentFromProgress,
} from '@/config/workflowSteps';
import { V7_ANALYSIS_PIPELINE_STEPS } from '@/config/v7AnalysisPipeline';
import type { StatusResponse, StreamEvent } from '@/types/analysis';
import {
  clearInflightAnalysis,
  setInflightAnalysis,
} from '@/utils/inflightAnalysisStorage';

type UseAnalysisProgressOptions = {
  onComplete?: (analysisSessionId: string) => void;
  onError?: (message: string) => void;
  onCancelled?: () => void;
};

const POLL_INTERVAL_MS = 2000;

function applyStatusProgress(
  status: StatusResponse,
  setters: {
    setCompletedAgents: Dispatch<SetStateAction<string[]>>;
    setCompletedPhases: Dispatch<SetStateAction<string[]>>;
    setCompletedStepIds: Dispatch<SetStateAction<string[]>>;
    setFailedStepIds: Dispatch<SetStateAction<string[]>>;
    setCurrentAgent: Dispatch<SetStateAction<string | null>>;
    setMultiStrandStatus: Dispatch<SetStateAction<string | null>>;
    setStrandId: Dispatch<SetStateAction<string | null>>;
    setStrandPhase: Dispatch<SetStateAction<string | null>>;
    setStrandIds: Dispatch<SetStateAction<string[]>>;
    setDominanceBand: Dispatch<SetStateAction<string | null>>;
    setWarning: Dispatch<SetStateAction<string | null>>;
    setScorecardPending: Dispatch<SetStateAction<boolean>>;
    setCurrentStepId: Dispatch<SetStateAction<string | null>>;
  },
) {
  const phases = status.completed_phases?.length
    ? status.completed_phases
    : status.completed_step_ids;
  if (status.completed_agents) {
    setters.setCompletedAgents(status.completed_agents);
  }
  if (phases) {
    setters.setCompletedPhases(phases);
  }
  if (status.completed_step_ids) {
    setters.setCompletedStepIds(status.completed_step_ids);
  }
  if (status.failed_step_ids) {
    setters.setFailedStepIds(status.failed_step_ids);
  }
  if (status.current_step_id !== undefined) {
    setters.setCurrentStepId(status.current_step_id ?? null);
  }
  if (status.status === 'processing') {
    // Prefer clean methodology agent ids; ignore garbage strand:step as current_agent
    const rawAgent = status.current_agent || null;
    const cleanAgent =
      rawAgent && /^\d+\.\d+[a-z]?/.test(rawAgent) ? rawAgent : null;
    const inferred = inferCurrentAgentFromProgress({
      currentAgent: cleanAgent,
      completedPhases: phases,
      completedAgents: status.completed_agents,
    });
    setters.setCurrentAgent(inferred);
  }
  if (status.multi_strand_status !== undefined) {
    setters.setMultiStrandStatus(status.multi_strand_status ?? null);
  }
  if (status.strand_id !== undefined) {
    setters.setStrandId(status.strand_id ?? null);
  }
  if (status.strand_phase !== undefined) {
    setters.setStrandPhase(status.strand_phase ?? null);
  }
  if (status.strand_ids) {
    setters.setStrandIds(status.strand_ids);
  }
  if (status.dominance_band !== undefined) {
    setters.setDominanceBand(status.dominance_band ?? null);
  }
  if (status.warning || status.status_detail === 'completed_with_errors') {
    setters.setWarning(
      status.warning ||
        (status.failed_step_ids?.length
          ? `${status.failed_step_ids.length} step(s) failed after executor fall-through`
          : null),
    );
  }
  if (status.scorecard_pending !== undefined) {
    setters.setScorecardPending(Boolean(status.scorecard_pending));
  }
}

function applyMethodologyProgress(
  event: Extract<StreamEvent, { type: 'methodology_progress' }>,
  setters: Parameters<typeof applyStatusProgress>[1],
) {
  applyStatusProgress(
    {
      session_id: event.session_id || '',
      status: 'processing',
      completed_agents: event.completed_agents,
      completed_phases: event.completed_phases || event.completed_step_ids,
      completed_step_ids: event.completed_step_ids,
      failed_step_ids: event.failed_step_ids,
      current_agent: event.current_agent,
      current_step_id: event.current_step_id,
      strand_id: event.strand_id,
      strand_phase: event.strand_phase,
      multi_strand_status: event.multi_strand_status,
      strand_ids: event.strand_ids,
      dominance_band: event.dominance_band,
      is_multi_strand: event.is_multi_strand,
      errors: event.errors,
    },
    setters,
  );
}

export function useAnalysisProgress(options: UseAnalysisProgressOptions = {}) {
  const flowAbortRef = useRef<AbortController | null>(null);
  const pollAbortRef = useRef(false);
  const activePollSessionRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const [analyzing, setAnalyzing] = useState(false);
  const [currentAgent, setCurrentAgent] = useState<string | null>(null);
  const [completedAgents, setCompletedAgents] = useState<string[]>([]);
  const [completedPhases, setCompletedPhases] = useState<string[]>([]);
  const [completedStepIds, setCompletedStepIds] = useState<string[]>([]);
  const [failedStepIds, setFailedStepIds] = useState<string[]>([]);
  const [analysisSessionId, setAnalysisSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [multiStrandStatus, setMultiStrandStatus] = useState<string | null>(null);
  const [strandId, setStrandId] = useState<string | null>(null);
  const [strandPhase, setStrandPhase] = useState<string | null>(null);
  const [strandIds, setStrandIds] = useState<string[]>([]);
  const [dominanceBand, setDominanceBand] = useState<string | null>(null);
  const [scorecardPending, setScorecardPending] = useState(false);
  const [currentStepId, setCurrentStepId] = useState<string | null>(null);

  const progressSetters = {
    setCompletedAgents,
    setCompletedPhases,
    setCompletedStepIds,
    setFailedStepIds,
    setCurrentAgent,
    setMultiStrandStatus,
    setStrandId,
    setStrandPhase,
    setStrandIds,
    setDominanceBand,
    setWarning,
    setScorecardPending,
    setCurrentStepId,
  };

  const currentAgentLabel = agentLabel(currentAgent);
  const analyseSubtext = buildAnalyseSubtext({
    analyzing,
    currentAgent,
    completedPhases,
    completedAgents,
    multiStrandStatus,
    strandId,
    strandPhase,
    currentStepId,
  });

  const reset = useCallback(() => {
    flowAbortRef.current?.abort();
    pollAbortRef.current = true;
    activePollSessionRef.current = null;
    clearInflightAnalysis();
    setAnalyzing(false);
    setCurrentAgent(null);
    setCompletedAgents([]);
    setCompletedPhases([]);
    setCompletedStepIds([]);
    setFailedStepIds([]);
    setAnalysisSessionId(null);
    setError(null);
    setWarning(null);
    setMultiStrandStatus(null);
    setStrandId(null);
    setStrandPhase(null);
    setStrandIds([]);
    setDominanceBand(null);
    setScorecardPending(false);
    setCurrentStepId(null);
  }, []);

  const applyStatus = useCallback((status: StatusResponse) => {
    applyStatusProgress(status, progressSetters);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setters are stable useState dispatchers
  }, []);

  const pollUntilDone = useCallback(
    async (sessionId: string) => {
      if (activePollSessionRef.current === sessionId) return;
      activePollSessionRef.current = sessionId;
      pollAbortRef.current = false;
      setAnalyzing(true);
      setAnalysisSessionId(sessionId);
      setError(null);
      const deadline = Date.now() + 3600000;

      try {
        while (Date.now() < deadline && !pollAbortRef.current) {
          try {
            const status = await getAnalysisStatus(sessionId);
            if (pollAbortRef.current) return;
            applyStatus(status);
            if (status.status === 'completed') {
              clearInflightAnalysis();
              if (mountedRef.current) {
                setAnalyzing(false);
                setCurrentAgent(null);
                if (status.warning) setWarning(status.warning);
              }
              optionsRef.current.onComplete?.(sessionId);
              return;
            }
            if (status.status === 'error') {
              const msg = status.error || 'Analysis failed';
              clearInflightAnalysis();
              if (mountedRef.current) {
                setError(msg);
                setAnalyzing(false);
                setCurrentAgent(null);
              }
              optionsRef.current.onError?.(msg);
              return;
            }
          } catch (pollErr: unknown) {
            console.warn('[useAnalysisProgress] poll error:', pollErr);
          }
          await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        }
        if (!pollAbortRef.current && mountedRef.current) {
          setAnalyzing(false);
          setError('Analysis status polling timed out');
        }
      } finally {
        if (activePollSessionRef.current === sessionId) {
          activePollSessionRef.current = null;
        }
      }
    },
    [applyStatus],
  );

  const startAnalysis = useCallback(
    async (params: {
      uploadSessionId: string;
      scriptText: string;
      gcsUri?: string;
      scriptName?: string;
      projectId?: string;
    }) => {
      flowAbortRef.current?.abort();
      pollAbortRef.current = true;
      const ac = new AbortController();
      flowAbortRef.current = ac;

      setError(null);
      setWarning(null);
      setAnalyzing(true);
      setCompletedAgents([]);
      setCompletedPhases([]);
      setCompletedStepIds([]);
      setFailedStepIds([]);
      setCurrentAgent(null);
      setStrandIds([]);
      setDominanceBand(null);
      setScorecardPending(true);
      // Methodology uses upload session id as analysis id — persist immediately so
      // navigate-away can resume before the first SSE "started" event.
      setInflightAnalysis({
        analysisSessionId: params.uploadSessionId,
        workflowSessionId: params.uploadSessionId,
        scriptTitle: params.scriptName || 'Script',
      });
      setAnalysisSessionId(params.uploadSessionId);

      const gcsUri = params.gcsUri?.startsWith('gs://')
        ? params.gcsUri
        : params.gcsUri
          ? `gs://${params.gcsUri.replace(/^gs:\/\//, '')}`
          : undefined;

      try {
        await analyzeScriptStream(
          params.scriptText,
          params.uploadSessionId,
          {
            onEvent: (event) => {
              if (event.type === 'agent_start') {
                const aid =
                  event.agent_name && /^\d+\.\d+[a-z]?/.test(event.agent_name)
                    ? event.agent_name
                    : event.step_id
                      ? (event.step_id.match(/(\d+\.\d+[a-z]?)/) || [])[1]
                      : null;
                if (aid) setCurrentAgent(aid);
                if (event.strand_id) setStrandId(event.strand_id);
                if (event.step_id) {
                  setCurrentStepId(
                    event.strand_id ? `${event.strand_id}:${event.step_id}` : event.step_id,
                  );
                  setStrandPhase(event.step_id);
                }
              } else if (event.type === 'agent_end') {
                setCompletedAgents((prev) =>
                  prev.includes(event.agent_name) ? prev : [...prev, event.agent_name],
                );
                const step = V7_ANALYSIS_PIPELINE_STEPS.find(
                  (s) => s.agentId === event.agent_name || s.id === event.agent_name,
                );
                if (step) {
                  setCompletedPhases((prev) =>
                    prev.includes(step.id) ? prev : [...prev, step.id],
                  );
                }
                if (event.step_id) {
                  setCompletedStepIds((prev) =>
                    prev.includes(event.step_id!) ? prev : [...prev, event.step_id!],
                  );
                  if (event.failed) {
                    setFailedStepIds((prev) =>
                      prev.includes(event.step_id!) ? prev : [...prev, event.step_id!],
                    );
                  }
                }
                setCurrentAgent((prev) => (prev === event.agent_name ? null : prev));
              } else if (event.type === 'started' || event.type === 'methodology_start') {
                setAnalysisSessionId(event.session_id);
                setInflightAnalysis({
                  analysisSessionId: event.session_id,
                  workflowSessionId: params.uploadSessionId,
                  scriptTitle: params.scriptName || 'Script',
                });
              } else if (event.type === 'methodology_progress') {
                applyMethodologyProgress(event, progressSetters);
                if (event.current_step_id) setCurrentStepId(event.current_step_id);
                if (event.strand_id) setStrandId(event.strand_id);
                if (event.strand_phase) setStrandPhase(event.strand_phase);
                if (event.current_agent && /^\d+\.\d+[a-z]?/.test(event.current_agent)) {
                  setCurrentAgent(event.current_agent);
                }
              } else if (event.type === 'progress_sync') {
                setCompletedAgents(event.completed_agents);
                if (event.completed_phases?.length) {
                  setCompletedPhases(event.completed_phases);
                }
              } else if (event.type === 'methodology_pending') {
                clearInflightAnalysis();
                const msg =
                  event.message ||
                  'Nuclius Index methodology is pending. Analysis agents are disabled.';
                if (mountedRef.current) {
                  setError(msg);
                  setAnalyzing(false);
                  setCurrentAgent(null);
                }
                optionsRef.current.onError?.(msg);
              } else if (event.type === 'complete') {
                if (event.status === 'methodology_pending') {
                  clearInflightAnalysis();
                  const msg =
                    'Nuclius Index methodology is pending. Analysis agents are disabled.';
                  if (mountedRef.current) {
                    setError(msg);
                    setAnalyzing(false);
                    setCurrentAgent(null);
                  }
                  optionsRef.current.onError?.(msg);
                  return;
                }
                if (event.status === 'failed' || event.status === 'error') {
                  clearInflightAnalysis();
                  const msg = 'Analysis failed after executor fall-through was exhausted.';
                  if (mountedRef.current) {
                    setError(msg);
                    setAnalyzing(false);
                    setCurrentAgent(null);
                  }
                  optionsRef.current.onError?.(msg);
                  return;
                }
                if (event.status === 'completed_with_errors') {
                  setWarning('Analysis finished with some failed steps (see details below).');
                }
                setAnalysisSessionId(event.session_id);
                clearInflightAnalysis();
                if (mountedRef.current) {
                  setAnalyzing(false);
                  setCurrentAgent(null);
                }
                optionsRef.current.onComplete?.(event.session_id);
              } else if (event.type === 'cancelled') {
                reset();
                optionsRef.current.onCancelled?.();
              } else if (event.type === 'error') {
                clearInflightAnalysis();
                if (mountedRef.current) {
                  setError(event.message || 'Analysis failed');
                  setAnalyzing(false);
                  setCurrentAgent(null);
                }
                optionsRef.current.onError?.(event.message || 'Analysis failed');
              }
            },
            signal: ac.signal,
          },
          gcsUri,
          params.scriptName,
          undefined,
          3600000,
          true,
          params.projectId,
        );
      } catch (err: any) {
        if (ac.signal.aborted) {
          reset();
          return;
        }
        setError(err.message || 'Analysis failed');
        setAnalyzing(false);
        setCurrentAgent(null);
        options.onError?.(err.message || 'Analysis failed');
      }
    },
    [options, reset],
  );

  const cancel = useCallback(async () => {
    const sid = analysisSessionId;
    flowAbortRef.current?.abort();
    pollAbortRef.current = true;
    activePollSessionRef.current = null;
    if (sid) {
      try {
        await cancelAnalysis(sid);
      } catch {
        /* non-fatal */
      }
    }
    reset();
    options.onCancelled?.();
  }, [analysisSessionId, options, reset]);

  const resumeIfInProgress = useCallback(
    async (
      sessionId: string | undefined | null,
      meta?: { scriptTitle?: string; workflowSessionId?: string },
    ) => {
      if (!sessionId) return;
      setInflightAnalysis({
        analysisSessionId: sessionId,
        scriptTitle: meta?.scriptTitle,
        workflowSessionId: meta?.workflowSessionId,
      });
      setAnalyzing(true);
      setAnalysisSessionId(sessionId);
      setError(null);
      try {
        const status = await getAnalysisStatus(sessionId);
        if (status.status === 'completed') {
          clearInflightAnalysis();
          if (mountedRef.current) {
            setAnalyzing(false);
            setCurrentAgent(null);
            if (status.warning) setWarning(status.warning);
          }
          optionsRef.current.onComplete?.(sessionId);
          return;
        }
        if (status.status === 'error') {
          const msg = status.error || 'Analysis failed';
          clearInflightAnalysis();
          if (mountedRef.current) {
            setError(msg);
            setAnalyzing(false);
            setCurrentAgent(null);
          }
          optionsRef.current.onError?.(msg);
          return;
        }
        if (status.status !== 'processing') {
          clearInflightAnalysis();
          if (mountedRef.current) setAnalyzing(false);
          return;
        }
        applyStatus(status);
      } catch (err: unknown) {
        console.warn('[useAnalysisProgress] resume status fetch failed:', err);
        clearInflightAnalysis();
        if (mountedRef.current) setAnalyzing(false);
        return;
      }
      void pollUntilDone(sessionId);
    },
    [applyStatus, pollUntilDone],
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      pollAbortRef.current = true;
    };
  }, []);

  return {
    analyzing,
    currentAgent,
    currentAgentLabel,
    analyseSubtext,
    completedAgents,
    completedPhases,
    completedStepIds,
    failedStepIds,
    analysisSessionId,
    error,
    warning,
    multiStrandStatus,
    strandId,
    strandPhase,
    strandIds,
    dominanceBand,
    scorecardPending,
    startAnalysis,
    cancel,
    resumeIfInProgress,
    reset,
    setAnalysisSessionId,
  };
}
